import {
  FileEnum,
  FileExtensionEnum,
} from '@protos/content_management/content.db_pb';
import { MimeEnum } from '@protos/learning_management/lms.common.apis_pb';

export function getImageType(fileType: string) {
  switch (fileType.toLowerCase()) {
    case 'image/png':
      return MimeEnum.MIME_PNG;
    case 'image/jpeg':
      return MimeEnum.MIME_JPEG;
    case 'image/jpg':
      return MimeEnum.MIME_JPG;
    case 'application/pdf':
      return MimeEnum.MIME_PDF;
    case 'application/msword':
      return MimeEnum.MIME_DOC;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return MimeEnum.MIME_DOCX;
    case 'application/vnd.ms-powerpoint':
      return MimeEnum.MIME_PPT;
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return MimeEnum.MIME_PPTX;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return MimeEnum.MIME_XLSX;
    case 'video/mp4':
      return MimeEnum.MIME_MP4;
    default:
      return MimeEnum.MIME_UNDEFINED;
  }
}

export function getFileType(fileType: string): FileEnum {
  switch (fileType.toLowerCase()) {
    case 'image/png':
    case 'image/jpeg':
    case 'image/jpg':
      return FileEnum.FILE_TYPE_IMAGE;
    case 'application/msword':
      return FileEnum.FILE_TYPE_DOCUMENT;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return FileEnum.FILE_TYPE_DOCUMENT;
    case 'application/vnd.ms-powerpoint':
      return FileEnum.FILE_TYPE_PRESENTATION;
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return FileEnum.FILE_TYPE_PRESENTATION;
    case 'application/pdf':
      return FileEnum.FILE_TYPE_PDF;
    case 'video/mp4':
      return FileEnum.FILE_TYPE_VIDEO;

    default:
      return FileEnum.FILE_TYPE_UNKNOWN;
  }
}

export function getFileTypeName(fileType: FileEnum | undefined): string {
  if (!fileType) return 'unknown_file';
  switch (fileType) {
    case FileEnum.FILE_TYPE_IMAGE:
      return 'image_icon';
    case FileEnum.FILE_TYPE_DOCUMENT:
      return 'document_paper';
    case FileEnum.FILE_TYPE_PRESENTATION:
      return 'powerpoint';
    case FileEnum.FILE_TYPE_PDF:
      return 'pdf_gray';
    case FileEnum.FILE_TYPE_VIDEO:
      return 'video';
    case FileEnum.FILE_TYPE_AUDIO:
      return 'audio';
    default:
      return 'unknown_file';
  }
}

export function getFileExtensionType(fileExtension: string) {
  switch (fileExtension.toLowerCase()) {
    case 'image/jpg':
      return FileExtensionEnum.FILE_EXTENSION_JPG;
    case 'image/jpeg':
      return FileExtensionEnum.FILE_EXTENSION_JPEG;
    case 'image/png':
      return FileExtensionEnum.FILE_EXTENSION_PNG;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return FileExtensionEnum.FILE_EXTENSION_DOCX;
    case 'application/msword':
      return FileExtensionEnum.FILE_EXTENSION_DOC;
    // todo: PPTX vs PPT ??
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return FileExtensionEnum.FILE_EXTENSION_PPTX;

    case 'application/vnd.ms-powerpoint':
      return FileExtensionEnum.FILE_EXTENSION_PPT;
    case 'application/pdf':
      return FileExtensionEnum.FILE_EXTENSION_PDF;
    case 'video/mp4':
      return FileExtensionEnum.FILE_EXTENSION_MP4;
    default:
      return FileExtensionEnum.FILE_EXTENSION_UNKNOWN;
  }
}

// =============================================================
// Resource uploads only. The backend signs media and packages only for
// FILE_UPLOAD_TEACHER_RESOURCE, so homework/question uploads keep the helpers above.

interface IResourceUploadType {
  mimeType: MimeEnum;
  // Must equal the type the backend signs the URL for, or GCS rejects the PUT.
  contentType: string;
  fileType: FileEnum;
  fileExtensionType: FileExtensionEnum;
}

const resourceUploadTypes: Record<string, IResourceUploadType> = {
  png: {
    mimeType: MimeEnum.MIME_PNG,
    contentType: 'image/png',
    fileType: FileEnum.FILE_TYPE_IMAGE,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_PNG,
  },
  jpeg: {
    mimeType: MimeEnum.MIME_JPEG,
    contentType: 'image/jpeg',
    fileType: FileEnum.FILE_TYPE_IMAGE,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_JPEG,
  },
  jpg: {
    mimeType: MimeEnum.MIME_JPG,
    contentType: 'image/jpg',
    fileType: FileEnum.FILE_TYPE_IMAGE,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_JPG,
  },
  pdf: {
    mimeType: MimeEnum.MIME_PDF,
    contentType: 'application/pdf',
    fileType: FileEnum.FILE_TYPE_PDF,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_PDF,
  },
  doc: {
    mimeType: MimeEnum.MIME_DOC,
    contentType: 'application/msword',
    fileType: FileEnum.FILE_TYPE_DOCUMENT,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_DOC,
  },
  docx: {
    mimeType: MimeEnum.MIME_DOCX,
    contentType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    fileType: FileEnum.FILE_TYPE_DOCUMENT,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_DOCX,
  },
  ppt: {
    mimeType: MimeEnum.MIME_PPT,
    contentType: 'application/vnd.ms-powerpoint',
    fileType: FileEnum.FILE_TYPE_PRESENTATION,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_PPT,
  },
  pptx: {
    mimeType: MimeEnum.MIME_PPTX,
    contentType:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    fileType: FileEnum.FILE_TYPE_PRESENTATION,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_PPTX,
  },
  mp4: {
    mimeType: MimeEnum.MIME_MP4,
    contentType: 'video/mp4',
    fileType: FileEnum.FILE_TYPE_VIDEO,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_MP4,
  },
  mp3: {
    mimeType: MimeEnum.MIME_MP3,
    contentType: 'audio/mpeg',
    fileType: FileEnum.FILE_TYPE_AUDIO,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_MP3,
  },
  wav: {
    mimeType: MimeEnum.MIME_WAV,
    contentType: 'audio/wav',
    fileType: FileEnum.FILE_TYPE_AUDIO,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_WAV,
  },
  html: {
    mimeType: MimeEnum.MIME_HTML,
    contentType: 'text/html',
    fileType: FileEnum.FILE_TYPE_HTML,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_HTML,
  },
  // A plain .zip is always treated as a SCORM package; .h5p is typed separately.
  zip: {
    mimeType: MimeEnum.MIME_ZIP,
    contentType: 'application/zip',
    fileType: FileEnum.FILE_TYPE_SCORM,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_ZIP,
  },
  h5p: {
    mimeType: MimeEnum.MIME_ZIP,
    contentType: 'application/zip',
    fileType: FileEnum.FILE_TYPE_H5P,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_H5P,
  },
  // Stored as one file like a PDF; the backend signs the PUT for this exact type.
  epub: {
    mimeType: MimeEnum.MIME_EPUB,
    contentType: 'application/epub+zip',
    fileType: FileEnum.FILE_TYPE_EPUB,
    fileExtensionType: FileExtensionEnum.FILE_EXTENSION_EPUB,
  },
};

const resourceMimeTypeKeys: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/jpg': 'jpg',
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'video/mp4': 'mp4',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'text/html': 'html',
  'application/zip': 'zip',
  'application/x-zip-compressed': 'zip',
  'application/epub+zip': 'epub',
};

const resourceExtensionKeys: Record<string, string> = {
  png: 'png',
  jpeg: 'jpeg',
  jpg: 'jpg',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'docx',
  ppt: 'ppt',
  pptx: 'pptx',
  mp4: 'mp4',
  mp3: 'mp3',
  wav: 'wav',
  html: 'html',
  htm: 'html',
  zip: 'zip',
  h5p: 'h5p',
  epub: 'epub',
};

export const resourceUploadAccept = Object.keys(resourceExtensionKeys)
  .map((extension) => `.${extension}`)
  .join(',');

// Returns undefined when the file type isn't supported for resources.
export function getResourceUploadType(
  file: File
): IResourceUploadType | undefined {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  // .h5p and .epub are zips, which browsers report as a zip or with no type at all.
  if (extension === 'h5p') return resourceUploadTypes['h5p'];
  if (extension === 'epub') return resourceUploadTypes['epub'];
  // file.type is often empty or vendor-specific, so fall back to the extension.
  const key =
    resourceMimeTypeKeys[file.type.toLowerCase()] ||
    resourceExtensionKeys[extension];
  return key ? resourceUploadTypes[key] : undefined;
}

// Resource upload as it worked before all-type uploads (images, PDF, Word, PowerPoint):
// generic MIME detection and no explicit Content-Type, for schools without the demo.
export function getLegacyUploadType(
  file: File
): IResourceUploadType | undefined {
  const fileType = getFileType(file.type);
  if (fileType === FileEnum.FILE_TYPE_UNKNOWN) return undefined;
  return {
    mimeType: getImageType(file.type),
    contentType: '',
    fileType,
    fileExtensionType: getFileExtensionType(file.type),
  };
}

// Mirrors the backend check; the backend stays the authority.
export const isYoutubeUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return (
      url.length <= 2048 &&
      parsed.protocol === 'https:' &&
      ['www.youtube.com', 'youtube.com', 'm.youtube.com', 'youtu.be'].includes(
        parsed.hostname
      )
    );
  } catch {
    return false;
  }
};

// =============================================================

export const getFileSizeInMB = (fileSizeInBytes: number) => {
  const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
  return Number(fileSizeInMB.toFixed(2));
};
